import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useQuery, useMutation } from '@apollo/client';

import { LOYALTY_TIER_2, LOYALTY_TIER_3, LOYALTY_TIER_4, LOYALTY_TIER_5 } from 'constants/clubEurostar';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_CUSTOMER_DETAILS, PROCESS_TIER_BENEFIT_UPGRADE } from 'store/queries';
import { productFamilyCodes } from 'types/productFamilyCodes';
import NotLoggedInRedirectButton from 'pages/BuyThalysPass/NotLoggedInRedirectButton';
import Modal from 'components/ui/Modal/Modal';
import BlockTicketsWithExtraLuggageWarning from '../../AfterSalesWarning/BlockTicketsWithExtraLuggageWarning/BlockTicketsWithExtraLuggageWarning';

import { StyledCtaButtons } from '../AfterSalesCtas.styles';
import {
  allSegmentsOfProductFamily,
  allSegmentsOfService,
  allSegmentsOfType,
  allSegmentsSegmentsDepartInFuture,
  runAfterSaleValidator,
  getSegmentsForItemRefs,
  getAssociatedItemRefsForSelectedItems,
  selectedItemsHaveLuggageExtras
} from '../AfterSalesCtas.utils';

const TierBenefitUpgradeCta = ({
  history: {
    push,
    location: { search }
  },
  history,
  label,
  disabled,
  selectedItemRefs,
  booking,
  isMenuOpen = false,
  areItemsInASummatedFare = false,
  setDisabled
}) => {
  const [shouldShowUpgradeModal, setShouldShowUpgradeModal] = useState(false);
  const [shouldShowNotPossibleUpgradeModal, setShouldShowNotPossibleUpgradeModal] = useState(false);
  const [showBlockTicketsWithExtraLuggageWarning, setShowBlockTicketsWithExtraLuggageWarning] = useState(false);

  const membershipNumber = new URLSearchParams(search).get('membershipNumber');

  const associatedItemsRefs = getAssociatedItemRefsForSelectedItems({
    booking,
    selectedItemRefs
  });

  const segments = [...getSegmentsForItemRefs(booking, [...selectedItemRefs, ...associatedItemsRefs])];

  const disableTierBenefitsForSummatedESSN =
    areItemsInASummatedFare && segments.some((segment) => segment.serviceType?.code === 'SN' || segment.serviceType?.code === 'SNCB');

  const { data: customerData, loading: customerInfoLoading } = useQuery(GET_CUSTOMER_DETAILS, {
    variables: {
      membershipNumber
    },
    skip: !membershipNumber
  });

  const [processTierBenefitUpgrade, { loading: processTierBenefitUpgradeLoading, error: processTierBenefitUpgradeError }] = useMutation(
    PROCESS_TIER_BENEFIT_UPGRADE,
    {
      onCompleted: ({ processTierBenefitUpgrade }) => {
        if (processTierBenefitUpgrade.success) {
          const params = new URLSearchParams();

          params.set('membershipNumber', membershipNumber);
          params.set('isTierBenefitUpgrade', 'true');

          push(`/exchange/${booking.reference}/pax?${params.toString()}`);
        } else {
          setShouldShowNotPossibleUpgradeModal(true);
        }
      }
    }
  );

  const hasLuggageExtras = selectedItemsHaveLuggageExtras(booking, selectedItemRefs);

  const customerProfile = customerData?.getCustomerDetails?.profile;
  const customerLoyaltyTierCode = customerProfile?.loyalty?.tier?.tierCode;
  const isTierBenefitUpgradePossible =
    selectedItemRefs.length === 1 &&
    ([LOYALTY_TIER_2, LOYALTY_TIER_3, LOYALTY_TIER_4, LOYALTY_TIER_5].includes(customerLoyaltyTierCode) || !membershipNumber) &&
    runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([
      allSegmentsSegmentsDepartInFuture,
      allSegmentsOfType(['STANDARD']),
      allSegmentsOfProductFamily([productFamilyCodes.PUB]),
      allSegmentsOfService([serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED])
    ]);

  const handleTierBenefitUpgrade = async () => {
    if (!membershipNumber) {
      setShouldShowUpgradeModal(true);
      return;
    }

    if (hasLuggageExtras) {
      setShowBlockTicketsWithExtraLuggageWarning(true);
      return;
    }

    try {
      const { reference, currency } = booking;

      const customerId = customerProfile?.customerId;

      await processTierBenefitUpgrade({
        variables: {
          bookingReference: reference,
          currency,
          customerId,
          itemRefs: selectedItemRefs
        }
      });
    } catch (e) {
      console.error(`There was an error with the Tier Benefit Upgrade: ${e.message}`);
    }
  };

  if (processTierBenefitUpgradeError) {
    throw new AgentPortalError(processTierBenefitUpgradeError);
  }

  const handleCloseUpgradeModal = () => setShouldShowUpgradeModal(false);
  const handleCloseNotPossibleModal = () => setShouldShowNotPossibleUpgradeModal(false);

  const hasLoadingStatus = processTierBenefitUpgradeLoading || customerInfoLoading;

  useEffect(() => {
    setDisabled(!isTierBenefitUpgradePossible || hasLoadingStatus || disableTierBenefitsForSummatedESSN);
  });

  const toggleBlockTicketsWithExtraLuggageWarning = () => {
    setShowBlockTicketsWithExtraLuggageWarning(!showBlockTicketsWithExtraLuggageWarning);
  };

  return (
    <>
      <StyledCtaButtons
        type="button"
        data-cy="tier-benefit-upgrade-cta"
        data-testid="tier-benefit-upgrade-cta"
        disabled={disabled}
        label={label}
        loading={hasLoadingStatus}
        onClick={handleTierBenefitUpgrade}
        isMenuOpen={isMenuOpen}
      >
        {label}
      </StyledCtaButtons>
      <Modal isOpen={shouldShowUpgradeModal} onClose={handleCloseUpgradeModal}>
        <NotLoggedInRedirectButton history={history} />
      </Modal>
      <BlockTicketsWithExtraLuggageWarning
        isOpen={showBlockTicketsWithExtraLuggageWarning}
        closeModal={toggleBlockTicketsWithExtraLuggageWarning}
      />
      <Modal
        isOpen={shouldShowNotPossibleUpgradeModal}
        onClose={handleCloseNotPossibleModal}
        alert={<FormattedMessage id="tierBenefirUpgrade.allUpgradesHaveBeenUsed" />}
      ></Modal>
    </>
  );
};

TierBenefitUpgradeCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  history: historyShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.element,
  disabled: PropTypes.bool,
  isThroughFare: PropTypes.bool,
  isMenuOpen: PropTypes.bool,
  setDisabled: PropTypes.func,
  areItemsInASummatedFare: PropTypes.bool
};

export default TierBenefitUpgradeCta;
